import { extractRolesFromToken, TokenClaims } from '../utils/jwtUtils';
import { config } from '../utils/config';

const tokenCache = {
  accessToken: null as string | null,
  roles: null as string[] | null,
  timestamp: 0,
  expiresAt: 0
};

export interface CurrentUser {
  name: string;
  email: string;
  username: string;
  oid: string;
  tenantId: string;
}

let currentUserCache: CurrentUser | null = null;

const getUserFromMsal = (): CurrentUser | null => {
  const msal = (window as any).msalInstance;
  if (!msal) {
    return null;
  }

  let activeAccount = msal.getActiveAccount?.();
  if (!activeAccount) {
    const accounts = msal.getAllAccounts?.() || [];
    if (accounts.length > 0) {
      msal.setActiveAccount?.(accounts[0]);
      activeAccount = accounts[0];
    }
  }

  const claims = activeAccount?.idTokenClaims as any;
  if (!claims) {
    return null;
  }

  return {
    name: claims.name || claims.preferred_username || 'Usuario',
    email: claims.email || claims.upn || '',
    username: claims.preferred_username || claims.upn || claims.name || '',
    oid: claims.oid || '',
    tenantId: claims.tid || ''
  };
};

export const setCurrentUser = (user: CurrentUser | null): void => {
  currentUserCache = user;
};

export const clearCurrentUser = (): void => {
  currentUserCache = null;
};

export const getCurrentUser = (): CurrentUser | null => {
  if (currentUserCache) {
    return currentUserCache;
  }

  const userFromMsal = getUserFromMsal();
  if (userFromMsal) {
    currentUserCache = userFromMsal;
  }

  return userFromMsal;
};

export const getCurrentUsername = (fallback: string = config.USER_FAKE): string => {
  return getCurrentUser()?.username || fallback;
};

/**
 * Función utilitaria para obtener roles sin usar el hook (para usar fuera de componentes React)
 * IMPORTANTE: Esta función ahora es ASÍNCRONA porque necesita obtener el Access Token de APIM
 * @param msalInstance - Instancia de MSAL (opcional, si no se proporciona intentará obtenerla de window)
 * @param forceRefresh - Forzar actualización del cache (default: false)
 */
export const getUserRolesSync = async (msalInstance?: any, forceRefresh: boolean = false): Promise<string[]> => {
  try {
    
    // Verificar cache primero (si no se fuerza refresh)
    if (!forceRefresh && tokenCache.roles && tokenCache.expiresAt > Date.now()) {
      return tokenCache.roles;
    }
    
    console.log('Cache no válido o forceRefresh=true, obteniendo nuevos roles...');
    
    // Intentar obtener la instancia de MSAL
    let instance = msalInstance;
    
    if (!instance) {
      // Intentar obtener desde window (si está disponible globalmente)
      instance = (window as any).msalInstance;
    }
    
    if (!instance) {
      console.warn('No se pudo obtener la instancia de MSAL');
      return [];
    }

    const activeAccount = instance.getActiveAccount();
    
    if (!activeAccount) {
      console.warn('No hay cuenta activa');
      return [];
    }

    // Obtener el Access Token de APIM (donde están los roles)
    const apimClientId = (window as any).runtimeConfig?.APIM_CLIENT_ID || 
              (window as any).config?.APIM_CLIENT_ID ||
              config.APIM_CLIENT_ID;
    
    if (!apimClientId) {
      console.warn('No se encontró APIM_CLIENT_ID en la configuración');
      return [];
    }

    const tokenRequest = {
      scopes: [`api://${apimClientId}/access_as_user`],
      account: activeAccount,
      // Usar cache de MSAL para evitar llamadas innecesarias
      forceRefresh: forceRefresh
    };

    try {
      const response = await instance.acquireTokenSilent(tokenRequest);
      
      if (response.accessToken) {
        const roles = extractRolesFromToken(response.accessToken);
        
        // Actualizar cache
        tokenCache.accessToken = response.accessToken;
        tokenCache.roles = roles;
        tokenCache.timestamp = Date.now();
        
        // Calcular expiración (5 minutos antes de la expiración real del token)
        const tokenClaims = extractRolesFromToken(response.accessToken);
        const exp = (response.expiresOn?.getTime() || Date.now() + 3600000) - 300000; // 5 min antes
        tokenCache.expiresAt = exp;
        
        console.log('Roles extraídos del Access Token de APIM:', roles);
        return roles;
      }
    } catch (error) {
      console.warn('Error al obtener Access Token de APIM:', error);
      
      // Como fallback, intentar buscar en el ID Token (por si acaso)
      if (activeAccount.idTokenClaims) {
        const claims = activeAccount.idTokenClaims as TokenClaims;
        
        const possibleRoleClaims = ['roles', 'groups', 'extension_Roles', 'app_roles', 'role'];
        
        for (const claimName of possibleRoleClaims) {
          if (claims[claimName] && Array.isArray(claims[claimName])) {
            console.log(`Roles encontrados en ID Token (fallback) '${claimName}':`, claims[claimName]);
            
            // Actualizar cache con roles del ID Token
            tokenCache.roles = claims[claimName];
            tokenCache.timestamp = Date.now();
            tokenCache.expiresAt = Date.now() + 300000; // 5 minutos
            
            return claims[claimName];
          }
        }
      }
    }

    console.warn('❌ No se encontraron roles en ningún token');
    return [];
  } catch (error) {
    console.error('Error al obtener roles sincrónicamente:', error);
    return [];
  }
};

/**
 * Función para limpiar el cache de tokens (útil para logout)
 */
export const clearTokenCache = () => {
  tokenCache.accessToken = null;
  tokenCache.roles = null;
  tokenCache.timestamp = 0;
  tokenCache.expiresAt = 0;
  console.log('🧹 Cache de tokens limpiado');
};
